import React, { useEffect, useRef } from 'react'
import '../styles/ParticleBackground.css'

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  radius: number
  color: string
  opacity: number
}

// Spatial hash grid for O(1) neighbor lookups instead of O(n²)
class SpatialGrid {
  private cellSize: number
  private grid: Map<string, Particle[]>

  constructor(cellSize: number) {
    this.cellSize = cellSize
    this.grid = new Map()
  }

  private getKey(x: number, y: number): string {
    const cellX = Math.floor(x / this.cellSize)
    const cellY = Math.floor(y / this.cellSize)
    return `${cellX},${cellY}`
  }

  clear() {
    this.grid.clear()
  }

  insert(particle: Particle) {
    const key = this.getKey(particle.x, particle.y)
    if (!this.grid.has(key)) {
      this.grid.set(key, [])
    }
    this.grid.get(key)!.push(particle)
  }

  // Get particles in the same cell and neighboring cells
  getNearbyParticles(x: number, y: number): Particle[] {
    const nearby: Particle[] = []
    const cellX = Math.floor(x / this.cellSize)
    const cellY = Math.floor(y / this.cellSize)

    // Check 3x3 grid of cells around the particle
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const key = `${cellX + dx},${cellY + dy}`
        const cell = this.grid.get(key)
        if (cell) {
          nearby.push(...cell)
        }
      }
    }
    return nearby
  }
}

export function ParticleBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const particlesRef = useRef<Particle[]>([])
  const interactionParticleRef = useRef<Particle | null>(null)
  const animationFrameRef = useRef<number>(0)
  const mouseIsDownRef = useRef(false)
  const createIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const spatialGridRef = useRef<SpatialGrid | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d', { willReadFrequently: false })
    if (!ctx) return

    // Configuration
    const options = {
      velocity: 1,
      density: 12000, // lower = denser
      netLineDistance: 180,
      netLineColor: 'rgba(205, 18, 138, 1)',
      particleColors: ['rgba(205, 18, 138, 0.9)', 'rgba(180, 20, 120, 0.9)', 'rgba(220, 50, 150, 0.9)'],
      spawnQuantity: 3,
    }

    // Initialize spatial grid with cell size equal to connection distance
    spatialGridRef.current = new SpatialGrid(options.netLineDistance)

    const getLimitedRandom = (min: number, max: number) => {
      return Math.random() * (max - min) + min
    }

    const returnRandomArrayItem = <T,>(array: T[]): T => {
      return array[Math.floor(Math.random() * array.length)]
    }

    const sizeCanvas = () => {
      canvas.width = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
    }

    const createParticle = (x?: number, y?: number): Particle => {
      return {
        x: x ?? Math.random() * canvas.width,
        y: y ?? Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * options.velocity,
        vy: (Math.random() - 0.5) * options.velocity,
        radius: getLimitedRandom(1.5, 2.5),
        color: returnRandomArrayItem(options.particleColors),
        opacity: 0,
      }
    }

    const createParticles = () => {
      particlesRef.current = []
      const quantity = Math.floor((canvas.width * canvas.height) / options.density)

      // Create all particles immediately
      for (let i = 0; i < quantity; i++) {
        const particle = createParticle()
        particle.opacity = 1 // Start fully visible
        particlesRef.current.push(particle)
      }
    }

    const createInteractionParticle = () => {
      const particle = createParticle()
      particle.vx = 0
      particle.vy = 0
      particle.opacity = 1
      interactionParticleRef.current = particle
      particlesRef.current.push(particle)
      return particle
    }

    const removeInteractionParticle = () => {
      const index = particlesRef.current.indexOf(interactionParticleRef.current!)
      if (index > -1) {
        particlesRef.current.splice(index, 1)
        interactionParticleRef.current = null
      }
    }

    const updateParticle = (particle: Particle) => {
      // Fade in
      if (particle.opacity < 1) {
        particle.opacity += 0.01
      } else {
        particle.opacity = 1
      }

      // Bounce off edges
      if (particle.x > canvas.width + 100 || particle.x < -100) {
        particle.vx = -particle.vx
      }
      if (particle.y > canvas.height + 100 || particle.y < -100) {
        particle.vy = -particle.vy
      }

      // Update position
      particle.x += particle.vx
      particle.y += particle.vy
    }

    const drawParticle = (particle: Particle) => {
      ctx.beginPath()
      ctx.fillStyle = particle.color
      ctx.globalAlpha = particle.opacity
      ctx.arc(particle.x, particle.y, particle.radius, 0, 2 * Math.PI)
      ctx.fill()
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.globalAlpha = 1

      const particles = particlesRef.current
      const grid = spatialGridRef.current!
      const netLineDistSq = options.netLineDistance * options.netLineDistance

      // Rebuild spatial grid each frame
      grid.clear()
      for (let i = 0; i < particles.length; i++) {
        grid.insert(particles[i])
      }

      // Track which connections we've already drawn to avoid duplicates
      const drawnConnections = new Set<string>()

      // Draw connections using spatial grid for O(n) complexity
      for (let i = 0; i < particles.length; i++) {
        const p1 = particles[i]
        const nearby = grid.getNearbyParticles(p1.x, p1.y)

        for (let j = 0; j < nearby.length; j++) {
          const p2 = nearby[j]
          if (p1 === p2) continue

          // Create unique key for this connection
          const minIdx = Math.min(particles.indexOf(p1), particles.indexOf(p2))
          const maxIdx = Math.max(particles.indexOf(p1), particles.indexOf(p2))
          const connKey = `${minIdx}-${maxIdx}`
          
          if (drawnConnections.has(connKey)) continue
          drawnConnections.add(connKey)

          // Calculate squared distance (avoid sqrt for performance)
          const dx = p1.x - p2.x
          const dy = p1.y - p2.y
          const distSq = dx * dx + dy * dy
          
          if (distSq > netLineDistSq) continue

          const distance = Math.sqrt(distSq)
          ctx.beginPath()
          ctx.strokeStyle = options.netLineColor
          ctx.globalAlpha =
            ((options.netLineDistance - distance) / options.netLineDistance) *
            p1.opacity *
            p2.opacity *
            0.5
          ctx.lineWidth = 0.7
          ctx.moveTo(p1.x, p1.y)
          ctx.lineTo(p2.x, p2.y)
          ctx.stroke()
        }
      }

      // Update and draw particles
      for (let i = 0; i < particles.length; i++) {
        updateParticle(particles[i])
        drawParticle(particles[i])
      }

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    // Event handlers
    const handleMouseMove = (e: MouseEvent) => {
      if (!interactionParticleRef.current) {
        createInteractionParticle()
      }
      const rect = canvas.getBoundingClientRect()
      interactionParticleRef.current!.x = e.clientX - rect.left
      interactionParticleRef.current!.y = e.clientY - rect.top
    }

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault()
      if (!interactionParticleRef.current) {
        createInteractionParticle()
      }
      const rect = canvas.getBoundingClientRect()
      interactionParticleRef.current!.x = e.changedTouches[0].clientX - rect.left
      interactionParticleRef.current!.y = e.changedTouches[0].clientY - rect.top
    }

    const handleMouseDown = (e: MouseEvent) => {
      mouseIsDownRef.current = true
      
      // Spawn particles immediately on click
      const rect = canvas.getBoundingClientRect()
      const clickX = e.clientX - rect.left
      const clickY = e.clientY - rect.top
      
      for (let i = 0; i < options.spawnQuantity; i++) {
        const particle = createParticle(clickX, clickY)
        particle.opacity = 1
        particlesRef.current.push(particle)
      }

      // Continue spawning while mouse is held
      const spawnInterval = setInterval(() => {
        if (mouseIsDownRef.current && interactionParticleRef.current) {
          const particle = createParticle(
            interactionParticleRef.current.x,
            interactionParticleRef.current.y
          )
          particle.opacity = 1
          particlesRef.current.push(particle)
        } else {
          clearInterval(spawnInterval)
        }
      }, 50)
    }

    const handleMouseUp = () => {
      mouseIsDownRef.current = false
    }

    const handleMouseOut = () => {
      removeInteractionParticle()
    }

    const handleTouchEnd = (e: TouchEvent) => {
      e.preventDefault()
      removeInteractionParticle()
    }

    const handleResize = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      sizeCanvas()
      createParticles()
    }

    // Initialize
    sizeCanvas()
    createParticles()
    animate()

    // Bind events
    canvas.addEventListener('mousemove', handleMouseMove)
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false })
    canvas.addEventListener('mousedown', handleMouseDown)
    canvas.addEventListener('mouseup', handleMouseUp)
    canvas.addEventListener('mouseout', handleMouseOut)
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false })
    window.addEventListener('resize', handleResize)

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove)
      canvas.removeEventListener('touchmove', handleTouchMove)
      canvas.removeEventListener('mousedown', handleMouseDown)
      canvas.removeEventListener('mouseup', handleMouseUp)
      canvas.removeEventListener('mouseout', handleMouseOut)
      canvas.removeEventListener('touchend', handleTouchEnd)
      window.removeEventListener('resize', handleResize)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      if (createIntervalRef.current) {
        clearInterval(createIntervalRef.current)
      }
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="particle-canvas"
    />
  )
}